/**
 * Run controls, watch status, progress, and the live roster.
 *
 * These sat above Sentinel in the Live tab. They belong with the briefing
 * so the dock can keep the column for results.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Mission,
  MissionDeviceState,
  MissionProgress,
  activateNamedRoute,
  swapToAlternate,
  updateMission,
} from '../../services/missionApi';
import { formatMeters } from './format';
import MissionStartBrief from './MissionStartBrief';
import {
  Action,
  AssignedRoster,
  ProgressStrip,
  RunBar,
  WatchSettingsDialog,
} from './missionLiveParts';

export interface MissionPostureProps {
  mission: Mission;
  devices: MissionDeviceState[];
  progress: MissionProgress | null;
  deviceLabel: (deviceId: string) => string;
  units?: 'km' | 'mi';
  checking?: boolean;
  onRefresh?: () => void;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
  /** Opens setup so the start brief can send the operator back to the route. */
  onEdit?: () => void;
  /** Hide the start brief while setup or the planner is on top of it. */
  briefPaused?: boolean;
  settingsOpen?: boolean;
  onSettingsClose?: () => void;
  onEmergencyReroute?: () => void;
  onCancelEmergencyReroute?: () => void;
  emergencyReroutePicking?: boolean;
  emergencyRerouteBusy?: boolean;
  emergencyRerouteError?: string | null;
}

const MissionPosture: React.FC<MissionPostureProps> = ({
  mission,
  devices,
  progress,
  deviceLabel,
  units = 'mi',
  checking,
  onRefresh,
  onError,
  onMissionChange,
  onEdit,
  briefPaused,
  settingsOpen = false,
  onSettingsClose,
  onEmergencyReroute,
  onCancelEmergencyReroute,
  emergencyReroutePicking,
  emergencyRerouteBusy,
  emergencyRerouteError,
}) => {
  const [runBusy, setRunBusy] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [threshold, setThreshold] = useState(mission.deviation_threshold_m ?? 250);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => setThreshold(mission.deviation_threshold_m ?? 250), [mission.deviation_threshold_m]);

  // Baseline is the recorded test pass; the monitor watches it too.
  const running = mission.status === 'in_progress' || mission.status === 'baseline';
  const hasRoute = !!mission.route_geometry;
  const hasPhones = mission.assignees.length > 0;
  const monitorable = hasRoute && hasPhones;
  const alertsOn = mission.alerts_enabled ?? true;
  const watching = running && alertsOn && monitorable;
  const run = mission.current_run || null;
  const briefKey = run ? `mission-start-ack-${mission.id}-${run.id}` : null;

  useEffect(() => {
    if (!briefKey || typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(briefKey)) return;
    setBriefOpen(true);
  }, [briefKey]);

  const ackBrief = useCallback(() => {
    if (briefKey && typeof window !== 'undefined') {
      window.sessionStorage.setItem(briefKey, '1');
    }
    setBriefOpen(false);
  }, [briefKey]);

  useEffect(() => {
    if (!watching) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [watching]);

  const elapsedS = useMemo(() => {
    const startedAt = progress?.started_at || mission.started_at;
    if (!startedAt) return progress?.elapsed_s ?? null;
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return progress?.elapsed_s ?? null;
    return Math.max(0, Math.round((now - start) / 1000));
  }, [progress, mission.started_at, now]);

  const changeRun = useCallback(async (action: () => Promise<unknown>) => {
    setRunBusy(true);
    try {
      await action();
      onMissionChange();
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Could not change the run');
    } finally {
      setRunBusy(false);
    }
  }, [onError, onMissionChange]);

  if (!hasRoute) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Plan a route to watch this mission while it runs.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.25 }}>
      <RunBar
        run={run}
        runs={mission.runs || []}
        ready={monitorable}
        busy={runBusy}
        units={units}
        alternate={mission.alt_route}
        namedRoutes={mission.saved_routes || []}
        activeRouteId={mission.active_route_id}
        onSwap={() => changeRun(() => swapToAlternate(mission.id))}
        onActivateNamed={(id) => changeRun(() => activateNamedRoute(mission.id, id))}
        sentinelOff={!mission.sentinel_live_enabled}
        onToggleSentinel={() => changeRun(() => updateMission(mission.id, {
          sentinel_live_enabled: !mission.sentinel_live_enabled,
        }))}
        onEmergencyReroute={onEmergencyReroute}
        onCancelEmergencyReroute={onCancelEmergencyReroute}
        emergencyReroutePicking={emergencyReroutePicking}
        emergencyRerouteBusy={emergencyRerouteBusy}
        emergencyRerouteError={emergencyRerouteError}
        emergencyRerouteEnabled={running || mission.status === 'baseline' || !!run}
      />

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
          WATCH
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {!alertsOn
            ? 'route watch off'
            : running
              ? (checking ? 'checking…' : `every minute · ${formatMeters(threshold, units)}`)
              : 'starts when a run does'}
        </Typography>
        {monitorable && running && onRefresh && (
          <Action onClick={onRefresh} disabled={checking}>Refresh</Action>
        )}
      </Box>

      {!hasPhones && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Assign someone to this mission to watch them against the route.
        </Typography>
      )}

      {progress && <ProgressStrip progress={progress} units={units} elapsedS={elapsedS} />}

      {monitorable && running && !progress && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Waiting for a position on the route before progress can be worked out.
        </Typography>
      )}

      <AssignedRoster
        devices={devices}
        deviceLabel={deviceLabel}
        units={units}
        empty={running && monitorable ? 'No positions reported yet for the assigned devices.' : undefined}
      />

      <WatchSettingsDialog
        open={settingsOpen}
        mission={mission}
        threshold={threshold}
        alertsOn={alertsOn}
        units={units}
        onThreshold={setThreshold}
        onClose={() => onSettingsClose?.()}
        onError={onError}
        onMissionChange={onMissionChange}
      />

      <MissionStartBrief
        open={briefOpen && !briefPaused}
        mission={mission}
        units={units}
        onAck={ackBrief}
        onEdit={onEdit}
      />
    </Box>
  );
};

export default MissionPosture;
